import type { BioBanks, OneLinerRegister } from '../types.js';

/**
 * German bio material for BaseChatFun synthetic profiles.
 *
 * Written the way German 19–26-year-olds actually fill a bio: one short
 * line, often a fragment, lots of lowercase, no final full stop, "bin" for
 * "ich bin", "kp", "hdl". The engine adds emoji and typing habits; nothing
 * here carries an emoji. Every bank is gender-neutral because the engine
 * picks from it for both genders (so "in der Pflege", not "Pflegefachfrau").
 */

const ONE_LINERS: Record<OneLinerRegister, string[]> = {
  funny: [
    'bin nur hier weil mir langweilig war',
    'kann nicht kochen aber super gut bestellen',
    'mein Bett und ich haben was Ernstes',
    'Lieblingssport: Augenrollen',
    'jeden Montag neue Diät, jeden Dienstag Döner',
    'mache {hobby} genau zweimal im Jahr',
    'aus {city}, ja der Dialekt ist echt',
    'erst Kaffee, dann reden wir',
    'Gym-Mitgliedschaft seit Januar, war zweimal da',
    'mehr Tabs offen als Gehirnzellen',
    'Sternzeichen {zodiac}, das erklärt einiges',
    'meine Pflanzen sterben trotzdem, ich geb mir Mühe',
    'bestehe zu 70 Prozent aus Kaffee',
    'antworte schnell, nur nicht heute',
    'schlechte Witze gibts gratis dazu',
    'erzähl mir deinen besten Flachwitz',
    'kann jeden Disney Song auswendig, leider',
    'Pünktlichkeit ist nicht so meins',
    'professioneller Overthinker mit Nebenjob',
    'tagsüber {job}, nachts Philosoph',
    'als {zodiac} hab ich eh immer recht',
    'Lieblingsessen: das was die anderen bestellt haben',
    'hab einen Plan, weiß nur nicht welchen',
    'mein Akku ist immer auf 3 Prozent',
    'Döner ohne Zwiebeln ist kein Döner',
    'sag nix gegen {team}, dann verstehen wir uns',
    'lerne seit drei Jahren Spanisch, kann hola sagen',
    'Schlafen ist mein Hobby, {hobby} mein zweites',
    'würde für Pommes Schranke alles tun',
    'eigentlich {trait}, außer vor dem ersten Kaffee',
    'kann Schränke aufbauen, nur mit drei Schrauben übrig',
    'Bahn fährt nicht, ich auch nicht',
    'die Bahn hat mich geduldig gemacht',
    'Spätzle oder Pommes? beides',
    'mein Hund hat mehr Freunde als ich',
    'Smalltalk Weltmeister, Kreisliga im Zurückschreiben',
    'bin wie das Wetter in {city}, unberechenbar',
    'sag Bescheid wenn du Snacks hast',
    'koche nur Nudeln, aber die richtig gut',
    'Plan für Samstag: absagen',
    'meine Mama findet mich toll',
    'zu alt für den Club, zu jung für den Schrebergarten',
    'nein ich will nicht über {school} reden',
    'Studium: {school}, Motivation: unauffindbar',
    'hab mal {hobby} probiert, war ein Fehler',
    'Aufstehen ist jeden Tag eine Lebensentscheidung',
    'zwischen Fitness und Pizza hin und hergerissen',
    'Pizza Hawaii ist okay, diskutier mit mir',
    'kenne alle Folgen auswendig, frag nicht welche Serie',
    'Lieblingsplatz: Sofa, zweitliebster: auch Sofa',
    'mehr Zimmerpflanzen als Freunde',
    'kann nicht tanzen, mach es trotzdem',
    'Mitbewohner sagt ich bin anstrengend, Mitbewohner hat recht',
    'Schwachstelle: jede Bäckerei',
    'bin der Grund warum es Bedienungsanleitungen gibt',
    'Sprachnachrichten über 5 Minuten sind meine Love Language',
    'meine Oma hält mich für einen Engel, lassen wir das so',
    '{trait} und hungrig, meistens beides',
    'wenn ich nicht antworte, schlafe ich',
    'mein Orientierungssinn existiert nicht',
    'Mathe war nie meins, Pizza teilen schon',
    'Sport? ja, gucken',
    'nehme alles ernst außer mich selbst',
    'grad am Rumgammeln, wie immer',
    'Frühaufsteher, wenn früh 11 Uhr heißt',
    'mein Seelentier ist ein Faultier',
    'ich und {hobby} gegen den Rest der Welt',
    'werde Millionär sobald ich Lotto spiele',
    'kann gut zuhören, vergesse aber alles',
    'trinke keinen Alkohol, nur Spezi in Mengen',
    'Lebensmotto: erstmal Snacks',
    'Einkaufsliste geschrieben, zuhause vergessen',
    'kann nicht flirten, nur peinlich lachen',
    'wer Ananas auf Pizza mag darf schreiben',
    'Montag ist kein echter Tag',
    'meine Playlist ist peinlich, ich steh dazu',
    'Lieblingsgemüse: Pommes',
    'beruflich {job}, privat Couchexperte',
    'mein Talent: im Kino einschlafen',
    'komme immer zu spät, aber mit Stil',
    'typisch {zodiac}, also bitte Geduld mitbringen',
    'ja ich komm aus {city}, nein ich kenn nicht jeden',
    'mit {team} leiden ist mein Cardio',
    'Spaghettieis ist ein Grundnahrungsmittel',
    'sag jeden Abend ich geh früh schlafen, lol',
    'bin noch im Flugmodus vom Wochenende',
    'Konto leer, Kopf voll',
    'mag Menschen, aber nur in kleinen Dosen',
    'Kaffee schwarz, Humor schwärzer',
    'Spülmaschine einräumen ist mein Kampfsport',
    'mein Kühlschrank und ich führen lange Gespräche',
    'ganz normal, also komisch',
    'kann dir sagen wo es den besten Döner in {city} gibt',
    'die Hälfte meiner Fotos ist Essen',
    'Wochenende ist zum Ausschlafen da, nicht für Termine',
    'hab aufgehört zu zählen wie oft ich mit {hobby} anfange',
    'mein Nachbar hört meine Musik mit, gern geschehen',
    'Sonntag heißt Jogginghose',
    'bin wie WLAN in der Bahn, mal da mal weg',
    'Pfandflaschen sammeln ist auch Sparen',
    'Lieblingswort: Feierabend',
    'esse Schokocreme mit dem Löffel und schäme mich nicht',
  ],
  catchy: [
    'kein Drama, nur Vibes',
    'erst Kaffee, dann Charme',
    'lieber echt als perfekt',
    'Rest erzähl ich dir selbst',
    'schreib einfach',
    'gute Vibes only',
    'kleine Stadt, große Träume',
    'Herz auf der Zunge',
    'laut lachen, leise leben',
    '{city} im Herzen',
    'zwischen {city} und Fernweh',
    'immer unterwegs, nie verloren',
    'easy going, aber nicht easy',
    '{trait} mit Stil',
    'mehr machen, weniger reden',
    'einfach ich',
    'nicht perfekt, aber echt',
    'Leben ist zu kurz für schlechten Kaffee',
    'Musik laut, Sorgen leise',
    'ehrlich, direkt, loyal',
    'wer fragt, gewinnt',
    'immer für Quatsch zu haben',
    'Nachtmensch',
    '{hobby} und gute Leute',
    'Gym, Arbeit, Familie',
    'Familie zuerst',
    'Loyalität über alles',
    'mach dein Ding',
    'Herzmensch',
    'chillig unterwegs',
    'Ruhe bewahren und {hobby}',
    'Respekt kriegt wer Respekt gibt',
    'keine Spielchen',
    'hier für gute Gespräche',
    'Smalltalk kann ich, Deep Talk lieber',
    'Fernweh und Heimweh gleichzeitig',
    'aus {city} mit Liebe',
    'Hauptsache glücklich',
    'weniger denken, mehr machen',
    'stilles Wasser',
    'großes Herz, kleine Geduld',
    '{zodiac} mit Herz',
    '{team} im Blut',
    'schreib mir was Nettes',
    'sei nett oder sei leise',
    'Sarkasmus fließend',
    'kurz gesagt: sympathisch',
    'Abenteuer gesucht',
    'Kaffee, Musik, gute Leute',
    'hier für Gespräche um 2 Uhr nachts',
    'Tagträumer',
    'bodenständig mit Fernweh',
    'Lachen ist Pflicht',
    'kein Bock auf Fake',
    'echt statt Filter',
    'ruhig aber nicht langweilig',
    'Nordlicht',
    'Ruhrpottkind',
    'Großstadtkind',
    'Dorfkind mit Großstadtträumen',
    'Kiezkind',
    'Herz in {city}',
    'immer ehrlich',
    'Lächeln kostet nix',
    'gute Musik, gute Leute, gutes Essen',
    'lass reden',
    'langsam ist auch ein Tempo',
    'nicht jeder muss mich mögen',
    'auf der Suche nach guten Gesprächen',
    'meistens gut drauf',
    'Sonnenkind',
    'Nachteule mit Plan',
    'Stimme laut, Herz offen',
    'mehr {hobby}, weniger Stress',
    '{job} mit Herz',
    'lieber Abenteuer als Ausreden',
    'leise Töne, große Wirkung',
    'Wellenlänge ist alles',
    'wenn du lachen kannst, passt das',
    'Kopf schon im Wochenende',
    'Sprachnachrichten willkommen',
    'Bauch vor Kopf',
    'Kopf vor Bauch, meistens',
    'guter Zuhörer',
    'gib mir Musik und ich bin happy',
    'Kopf in den Wolken, Füße am Boden',
    'jeden Tag ein bisschen besser',
    'ich bleib wie ich bin',
    'geradeaus',
    'unkompliziert',
    'lieber Sonnenuntergang als Partytrubel',
    'mal laut, mal leise',
    'mit Herz und Humor',
    'Kaffee zuerst',
    'mehr Meer bitte',
    'Ostsee statt Malle',
    'Heimat ist da wo die Leute sind',
    'Bass an, Kopf aus',
    'heute hier, morgen da',
    'Stadionluft und Currywurst',
  ],
  cringe: [
    'suche den Deckel zu meinem Topf',
    'vielleicht bist du ja mein Lieblingsmensch',
    'wie ein Überraschungsei, außen süß innen spannend',
    'nicht weiterscrollen, das Schicksal hat entschieden',
    'hab mein Herz verloren, hast du es gefunden',
    'Liebe ist wie WLAN, man sieht sie nicht aber man braucht sie',
    'mein Lächeln ist gratis, meine Zeit nicht',
    'Engel ohne Flügel',
    'nicht alle Helden tragen Umhänge',
    'leben, lachen, lieben',
    'Mama sagt ich bin was Besonderes',
    'Gangster mit Herz',
    'bin nicht kompliziert, du bist nur nicht auf meinem Level',
    'wer mich will muss mich nehmen wie ich bin',
    'zu süß für diese Welt',
    'träume nicht dein Leben, lebe deinen Traum',
    'Liebe ist das Einzige was zählt',
    'meine Augen erzählen Geschichten',
    'Herzensbrecher im Ruhestand',
    'mein Herz schlägt nur für {team}',
    'typisch {zodiac}, lieb mich oder lass es',
    'Diamant unter Steinen',
    'die Sterne haben uns zusammengeführt',
    'zwei Herzen, ein Takt',
    'dein neuer Lieblingsmensch',
    'Liebe deinen Nächsten, vor allem mich',
    'Gefühle sind wie Schmetterlinge',
    'Vergangenheit ist Geschichte, Zukunft ein Geheimnis',
    'Löwenherz',
    'der Wolf in einer Welt voller Schafe',
    'einsamer Wolf',
    'Krieger des Lichts',
    'Sternenstaub in den Adern',
    'kleine Maus mit großem Herzen',
    'süßer als Zucker',
    'ein Lächeln von dir und ich bin dabei',
    'gute Seele, böser Humor',
    'treu wie ein Hund, frei wie ein Vogel',
    'bin das Kapitel das du nie vergisst',
    'Liebe ohne Grenzen',
    'nur Gott kann mich richten',
    'Familie ist alles, der Rest ist Bonus',
    'Vertrauen ist wie Glas, einmal kaputt immer kaputt',
    'wer mich verliert, verliert alles',
    'Herz aus Stein, wird nur für dich weich',
    'Romantiker in einer Welt voller Fakes',
    'Fake Freunde nein danke',
    'Liebe macht blind, zum Glück hab ich ne Brille',
    'bin wie Kaffee, stark und süß',
    'glaube an Liebe auf den ersten Blick',
    'Liebe auf den ersten Klick?',
    'dein Herz sucht mich, gib es zu',
    'das Leben ist ein Abenteuer, sei mein Begleiter',
    'Seelenverwandtschaft gesucht',
    'Mondkind',
    'Sternenkind',
    'im Herzen {zodiac}, im Kopf ein Rebell',
    'Chef von {city}',
    'Schmetterlinge im Bauch gesucht',
    'die Welt ist mein Spielplatz',
    'Liebe ist ein Gefühl, kein Spiel',
    'kein Spieler, nur ein Träumer',
    'mein Herz gehört Mama',
    'Blut ist dicker als Wasser',
    'mein Lächeln versteckt viel',
    'gefährlich süß',
    'zu ehrlich für diese Welt',
    'zu loyal für diese Zeit',
    'wahre Liebe stirbt nie',
    'stark wie ein Löwe, sanft wie ein Lamm',
    'Krone gerade, weiter gehts',
    'das Schicksal hat mich hergebracht',
    'vielleicht bin ich dein Happy End',
    'lachen bis der Bauch weh tut',
    'Herz aus Gold',
    'Unikat',
    'Original statt Kopie',
    'wenn du das liest, lächle mal',
    'Prinz oder Prinzessin, Hauptsache ehrlich',
    'meine Seele tanzt im Regen',
    'wer mich kennt, liebt mich',
    'nicht jeder versteht meinen Humor, nur die Besten',
    'von {city} bis zum Mond und zurück',
    'ich bin wie ich bin, und das ist gut so',
    'Gefühlschaos auf zwei Beinen',
    'Sonnenschein mit Gewitterwarnung',
    'meine Ex sagt ich bin toll, glaub ihr',
    'süß, aber mit Biss',
    'brauche keinen Filter, nur dich',
    'Liebe ist, wenn man trotzdem lacht',
    'jedes Herz hat seine Geschichte',
    'Träume groß, schlaf wenig',
    'Herzklopfen gesucht',
    'loyal bis zum Schluss',
    'Kopf sagt nein, Herz sagt ja',
    'Gefühle zeigen ist keine Schwäche',
    'ein Herz für Tiere und für dich',
    'du suchst Ehrlichkeit? gefunden',
    'Melodie im Herzen, Rhythmus im Blut',
  ],
  sincere: [
    'suche nette Leute zum Quatschen',
    'bin neu hier und schau mich mal um',
    'ehrliche Gespräche sind mir wichtig',
    'freue mich über jede nette Nachricht',
    'hier um neue Leute kennenzulernen',
    'brauche manchmal etwas bis ich auftaue',
    'Familie und Freunde bedeuten mir alles',
    'bin eher ruhig, aber wenn man mich kennt nicht mehr',
    'gerade umgezogen und kenne noch niemanden',
    'mag tiefe Gespräche und schlechte Witze',
    'versuche jeden Tag etwas Neues zu lernen',
    'arbeite viel, deshalb antworte ich manchmal spät',
    'einfach mal schauen was passiert',
    'Respekt und Ehrlichkeit sind mir wichtig',
    'abends oft im Voice Room, sag gern hallo',
    'suche Leute für gute Unterhaltungen',
    'kein Stress, einfach normal schreiben',
    'ich höre gern zu',
    'offen für alles, solange es respektvoll bleibt',
    'wer Lust auf Musik und Quatschen hat, gern melden',
    'mag ruhige Abende und gute Musik',
    'nach der Arbeit gern hier zum Abschalten',
    'schreib einfach, ich beiße nicht',
    'Freundschaft ist mir wichtiger als alles andere',
    'bin ein Familienmensch',
    'nicht so gut im Beschreiben, frag einfach',
    'mag Menschen mit Humor',
    'ich lache viel und gern',
    'Ehrlichkeit vor allem',
    'versuche positiv zu bleiben',
    'ich mag es gemütlich',
    'hier zum Reden und Lachen',
    'manchmal schüchtern, aber freundlich',
    'probier die App grad aus',
    'Musik hilft mir durch jeden Tag',
    'bin gern draußen in der Natur',
    'liebe meine Freunde über alles',
    'gute Laune ist ansteckend, ich versuchs',
    'suche keine Perfektion, nur Ehrlichkeit',
    'erzähl mir was von dir',
    'mag Leute die zuhören können',
    'langsam kennenlernen ist mir lieber',
    'meistens abends online',
    'ich mag Gespräche über Gott und die Welt',
    'freue mich auf neue Bekanntschaften',
    'hier zum Entspannen nach langen Schichten',
    'Schichtarbeit, deshalb zu komischen Zeiten online',
    'studiere noch und brauche manchmal Ablenkung',
    'ich gebe jedem eine Chance',
    'Respekt ist das Wichtigste',
    'dankbar für die kleinen Dinge',
    'mag Ehrlichkeit, auch wenn sie weh tut',
    'vermisse meine Heimat manchmal',
    'zwei Kulturen, ein Zuhause',
    'mit zwei Sprachen aufgewachsen',
    'gerade eine ruhigere Phase im Leben',
    'neue Stadt, neue Leute, neuer Anfang',
    'ich bin, wie ich bin',
    'mag Spaziergänge und lange Gespräche',
    'Sonntag ist Familientag',
    'ich koche gern für andere',
    'brauche Menschen, die mich zum Lachen bringen',
    'sag einfach hallo',
    'ich antworte jedem, versprochen',
    'Freundlichkeit kostet nichts',
    'versuche mehr unter Leute zu kommen',
    'kenne das Gefühl allein zu sein, deshalb bin ich hier',
    'nach einer Trennung wieder offen für Neues',
    'suche hier Freundschaften, alles andere ergibt sich',
    'mag Leute mit Herz',
    'Gespräche bis spät in die Nacht sind das Beste',
    'eher Zuhörer als Redner',
    'bin ehrlich und erwarte das auch',
    'gemeinsam lachen verbindet',
    'immer für ein gutes Gespräch zu haben',
    'ich glaube an das Gute im Menschen',
    'hier um Spaß zu haben und nett zu sein',
    'gerade viel Stress, hier zum Runterkommen',
    'mag Menschen, die wissen was sie wollen',
    'Natur, Musik und gute Gesellschaft',
    'im Herzen ein Kind geblieben',
    'versuche einfach ein guter Mensch zu sein',
    'in meiner Freizeit viel mit Freunden unterwegs',
    'hab ein offenes Ohr für jeden',
    'lieber wenige echte Freunde als viele falsche',
    'reise gern und lerne gern Leute kennen',
    'nicht kompliziert, einfach normal',
    'mag Abende mit Tee und Musik',
    'liebe meine Familie und meinen Job',
    'suche Leute mit ähnlichem Humor',
    'bin nicht oft online, aber wenn dann richtig',
    'rede gern über Musik, Filme und alles dazwischen',
    'keine Ahnung was ich hier schreiben soll, frag mich einfach',
  ],
};

const HOBBIES: string[] = [
  'Fitness', 'Gym', 'Kraftsport', 'Joggen', 'Laufen', 'Schwimmen', 'Fußball', 'Basketball', 'Volleyball', 'Handball',
  'Tennis', 'Tischtennis', 'Boxen', 'Kickboxen', 'Thaiboxen', 'Kampfsport', 'Yoga', 'Pilates', 'Wandern', 'Klettern',
  'Bouldern', 'Radfahren', 'Mountainbiken', 'Skaten', 'Snowboarden', 'Skifahren', 'Surfen', 'Kitesurfen', 'Reiten', 'Tanzen',
  'Salsa', 'Hip Hop tanzen', 'Zocken', 'Konsole', 'PC Games', 'Serien bingen', 'Filme', 'Kino', 'Anime', 'Manga',
  'Lesen', 'Bücher', 'Krimis', 'Podcasts', 'Musik', 'Deutschrap', 'Techno', 'Festivals', 'Konzerte', 'Raves',
  'Karaoke', 'Gitarre', 'Klavier', 'Singen', 'Schlagzeug', 'Beats bauen', 'Auflegen', 'Fotografie', 'Filmen', 'Zeichnen',
  'Malen', 'Nägel machen', 'Make-up', 'Mode', 'Shoppen', 'Second Hand', 'Flohmarkt', 'Kochen', 'Backen', 'Grillen',
  'Brunch', 'Kaffee', 'Matcha', 'Döner testen', 'Essen gehen', 'Sushi', 'Pizza', 'Reisen', 'Roadtrips', 'Camping',
  'Vanlife', 'Städtetrips', 'Strand', 'Ostsee', 'Nordsee', 'Berge', 'Natur', 'Spazieren', 'Gassi gehen', 'Pferde',
  'Katzen', 'Hunde', 'Gaming', 'PC bauen', 'Programmieren', 'Autos', 'Tuning', 'Motorrad', 'Schrauben', 'Autotreffen',
  'Angeln', 'Billard', 'Darts', 'Bowling', 'Kicker', 'Brettspiele', 'Escape Rooms', 'Schach', 'Sudoku', 'Puzzles',
  'Lego', 'Pflanzen', 'Garten', 'Balkon gärtnern', 'Upcycling', 'Nähen', 'Häkeln', 'Stricken', 'Töpfern', 'Tattoos',
  'Astrologie', 'Tarot', 'Meditation', 'Tagebuch schreiben', 'True Crime', 'Dokus', 'Geschichte', 'Sprachen lernen', 'Fußball gucken', 'Stadion',
  'Bundesliga', 'Formel 1', 'Eishockey', 'Calisthenics', 'Spinning', 'Trampolin', 'Inliner', 'Longboard', 'Stand Up Paddling', 'Segeln',
  'Tauchen', 'Shisha', 'Cafés', 'Sonnenuntergänge', 'Nachtspaziergänge', 'spontane Trips', 'Museen', 'Theater', 'Musicals', 'Stand Up Comedy',
  'Poetry Slam', 'Rap schreiben', 'Texte schreiben', 'Gedichte', 'Videos schneiden', 'Vinyl', 'lange Autofahrten', 'nachts Autofahren', 'Frühstücken', 'Ausschlafen',
  'Chillen', 'Freunde treffen', 'Familie', 'Weinabende', 'Cocktails', 'Feiern gehen', 'Afrobeats', 'K-Pop', 'türkische Musik', 'Arabesk',
  'Schlager', 'Metal', 'Punk', 'Indie', 'RnB', 'Hip Hop', 'Beachvolleyball', 'Golf', 'Badminton', 'Rudern',
  'Kanu fahren', 'Schlittschuh laufen', 'Sauna', 'Therme', 'Skincare', 'Haare färben', 'Sneaker', 'Streetwear', 'Vintage', 'Umdekorieren',
  'Heimwerken', 'Drohnen fliegen', 'Sterne gucken', 'Zoo', 'Aquarium', 'Torten backen', 'Plätzchen backen', 'Weihnachtsmärkte', 'Freibad', 'Baggersee',
  'Picknick', 'Rooftop Bars', 'Tee', 'Bubble Tea', 'Ramen', 'Streetfood', 'vegan kochen', 'Meal Prep', 'Basteln', 'Fahrrad reparieren',
];

const TRAITS: string[] = [
  'Nachteule', 'Frühaufsteher', 'chaotisch', 'ehrlich', 'loyal', 'direkt', 'ironisch', 'sarkastisch', 'tollpatschig', 'verpeilt',
  'ruhig', 'laut', 'spontan', 'verplant', 'ehrgeizig', 'faul', 'gemütlich', 'verrückt', 'lieb', 'frech',
  'schüchtern', 'offen', 'neugierig', 'romantisch', 'realistisch', 'verträumt', 'stur', 'entspannt', 'pingelig', 'humorvoll',
  'emotional', 'abenteuerlustig', 'zielstrebig', 'positiv', 'unentschlossen', 'geduldig', 'zuverlässig', 'introvertiert', 'extrovertiert', 'hilfsbereit',
  'chillig', 'albern', 'kindisch', 'dramatisch', 'nachdenklich', 'kreativ', 'sportlich', 'verfressen', 'Naschkatze', 'kaffeesüchtig',
  'tiefgründig', 'bodenständig', 'ehrlich bis zur Schmerzgrenze', 'harte Schale weicher Kern', 'Familienmensch', 'freiheitsliebend', 'heimatverbunden', 'weltoffen', 'selbstironisch', 'ungeduldig',
  'temperamentvoll', 'Hitzkopf', 'kitschig', 'nerdig', 'großes Herz', 'kleiner Chaot', 'Dickkopf', 'Sturkopf', 'Sonnenschein', 'Partymensch',
  'Couchpotato', 'Stubenhocker', 'Workaholic', 'Perfektionist', 'minimalistisch', 'nostalgisch', 'melancholisch', 'optimistisch', 'schlagfertig', 'charmant',
  'höflich', 'respektvoll', 'treu', 'unkompliziert', 'pflegeleicht', 'anstrengend aber lieb', 'aufgedreht', 'ausgeglichen', 'impulsiv', 'rational',
  'Bauchmensch', 'Kopfmensch', 'Overthinker', 'Morgenmuffel', 'planlos', 'organisiert', 'sensibel', 'empathisch', 'gesprächig', 'wortkarg',
  'zurückhaltend', 'selbstbewusst', 'trockener Humor', 'schwarzer Humor', 'Großmaul', 'Teddybär', 'Golden Retriever Energie', 'Katzenenergie', 'Tagträumer', 'Gewohnheitstier',
  'Quasselstrippe', 'Ruhepol', 'Kumpeltyp', 'Herzmensch', 'Hundemensch', 'Katzenmensch',
];

const CITIES: { name: string; weight: number }[] = [
  { name: 'Berlin', weight: 30 }, { name: 'Hamburg', weight: 18 }, { name: 'München', weight: 15 }, { name: 'Köln', weight: 12 },
  { name: 'Frankfurt', weight: 9 }, { name: 'Stuttgart', weight: 7 }, { name: 'Düsseldorf', weight: 7 }, { name: 'Dortmund', weight: 7 },
  { name: 'Leipzig', weight: 7 }, { name: 'Essen', weight: 6 }, { name: 'Bremen', weight: 6 }, { name: 'Hannover', weight: 6 },
  { name: 'Nürnberg', weight: 6 }, { name: 'Dresden', weight: 5 }, { name: 'Duisburg', weight: 5 }, { name: 'Bochum', weight: 4 },
  { name: 'Wuppertal', weight: 4 }, { name: 'Bielefeld', weight: 4 }, { name: 'Bonn', weight: 4 }, { name: 'Münster', weight: 4 },
  { name: 'Mannheim', weight: 4 }, { name: 'Karlsruhe', weight: 3 }, { name: 'Augsburg', weight: 3 }, { name: 'Wiesbaden', weight: 3 },
  { name: 'Gelsenkirchen', weight: 3 }, { name: 'Mönchengladbach', weight: 3 }, { name: 'Aachen', weight: 3 }, { name: 'Kiel', weight: 3 },
  { name: 'Freiburg', weight: 3 }, { name: 'Mainz', weight: 3 }, { name: 'Offenbach', weight: 2 }, { name: 'Ludwigshafen', weight: 2 },
  { name: 'Heilbronn', weight: 2 }, { name: 'Kassel', weight: 2 }, { name: 'Saarbrücken', weight: 2 }, { name: 'Rostock', weight: 2 },
];

const TEAMS: string[] = [
  'FC Bayern', 'FC Bayern, mia san mia', 'BVB', 'BVB, echte Liebe', 'Schalke 04', 'Schalke, Glück auf', 'HSV', 'nur der HSV',
  'Werder Bremen', 'Werder, lebenslang grün-weiß', 'Eintracht Frankfurt', 'die SGE', '1. FC Köln', 'der Effzeh', 'Bayer Leverkusen', 'die Werkself',
  'Union Berlin', 'Eisern Union', 'Hertha BSC', 'VfB Stuttgart', 'Gladbach', 'die Fohlen', 'St. Pauli', 'Fortuna Düsseldorf',
  'Hannover 96', 'der Club aus Nürnberg', 'Mainz 05', 'SC Freiburg', 'VfL Wolfsburg', 'Dynamo Dresden', 'Hansa Rostock', 'der Betze',
  'Galatasaray', 'Fenerbahçe', 'Beşiktaş', 'Trabzonspor', 'VfL Bochum', 'Arminia Bielefeld',
];

const SCHOOLS: string[] = [
  'Uni Köln', 'TU München', 'LMU', 'FU Berlin', 'HU Berlin', 'Uni Hamburg', 'RWTH Aachen', 'KIT', 'Goethe Uni', 'TU Dresden',
  'Uni Münster', 'Ruhr Uni Bochum', 'FH Dortmund', 'Uni Leipzig', 'Uni Mannheim', 'Uni Bonn', 'TU Berlin', 'Uni Duisburg-Essen',
  'studiere BWL', 'studiere Lehramt', 'studiere Soziale Arbeit', 'studiere Informatik', 'Medizinstudium', 'Jura', 'Psychologie',
  'Abi 2019', 'Abi 2021', 'Abi 2022', 'Abi 2023', 'Fachabi', 'Realschule durch', 'Ausbildung', 'Berufsschule', 'duales Studium',
  'FH', 'Master fast fertig', 'Bachelor durch', 'Erstsemester',
];

const JOBS: string[] = [
  'Pflege', 'Kita', 'Azubi bei der Bank', 'Azubi im Büro', 'KFZ Werkstatt', 'Elektrotechnik', 'Einzelhandel', 'Gastro',
  'Logistik', 'IT', 'Polizei', 'Rettungsdienst', 'Arztpraxis', 'Physiotherapie', 'Friseursalon', 'Lehramt',
  'Werkstudentenjob', 'Soziale Arbeit', 'Handwerk', 'Bau', 'Büro', 'Krankenhaus', 'Schichtdienst', 'Kosmetikstudio',
  'Fitnessstudio', 'Steuerbüro', 'selbstständig', 'Ausbildung im Handwerk', 'Lager', 'Altenpflege', 'Versicherung', 'Industrie',
  'Kundenservice', 'Marketing', 'Apotheke', 'Zahnarztpraxis',
];

const LYRICS: string[] = [
  'Atemlos durch die Nacht',
  'ein Hoch auf uns',
  'an Tagen wie diesen',
  'Haus am See',
  'nur noch kurz die Welt retten',
  'ohne dich schlaf ich heut Nacht nicht ein',
  'ich lass für dich das Licht an',
  'Lieblingsmensch',
  'je ne parle pas français',
  'Herz über Kopf',
  'was hat dich bloß so ruiniert',
  'Liebe ist alles',
  'ich war noch niemals in New York',
  'auf uns',
  'Applaus, Applaus',
  'Kopf hoch, Krone richten',
  'wer nicht wagt, der nicht gewinnt',
  'Hakuna Matata',
  'alles hat ein Ende, nur die Wurst hat zwei',
  'läuft bei mir',
  'lieber spät als nie',
  'Ende gut, alles gut',
  'Wolke 7',
  'ich bau ne Stadt für dich',
  'Junge, komm bald wieder',
  'Komet',
  'das Leben ist kein Ponyhof',
  'aus Fehlern wird man klug',
  'keine Panik auf der Titanic',
  'Leben und leben lassen',
  'immer wieder sonntags',
];

const PETS: string[] = [
  '2 Katzen', 'mein Hund Bruno zuerst', 'Katzen > Menschen', 'Hund > Menschen', '2 Katzen, 0 Plan', 'mein Kater Simba regiert',
  'Hund heißt Lucky', 'zwei Wellensittiche', 'Meerschweinchen x3', 'mein Hund kommt mit', 'Katze Luna ist der Boss',
  'Hund Rocky und ich', 'Bartagame namens Kevin', 'hab nen Labrador', 'Französische Bulldogge Mia', 'Pferdemensch seit immer',
  'Aquarium Nerd', 'mein Kater Garfield ist der Chef', 'Kaninchen Hoppel', 'Hund Balu, bester Mitbewohner', 'zwei Hunde, viel Chaos',
  'Katze aus dem Tierheim', 'Pflegehund Nala', 'Schildkröte Fritz, 20 Jahre alt',
];

export const DE_BIO: BioBanks = {
  oneLiners: ONE_LINERS,
  hobbies: HOBBIES,
  traits: TRAITS,
  cities: CITIES,
  teams: TEAMS,
  schools: SCHOOLS,
  jobs: JOBS,
  lyricsOrQuotes: LYRICS,
  pets: PETS,
  zodiac: {
    aries: 'Widder', taurus: 'Stier', gemini: 'Zwillinge', cancer: 'Krebs', leo: 'Löwe', virgo: 'Jungfrau',
    libra: 'Waage', scorpio: 'Skorpion', sagittarius: 'Schütze', capricorn: 'Steinbock', aquarius: 'Wassermann', pisces: 'Fische',
  },
  topicTemplates: {
    zodiac: ['{zodiac}', '{zodiac} halt', 'typisch {zodiac}', '{zodiac}, sorry im Voraus', '{zodiac} und stolz drauf'],
    team: ['{team}', 'mein Verein: {team}', 'Fußball und {team}', 'Herz für {team}'],
    school: ['{school}', '{school} und so', '{school}, Rest erzähl ich dir', 'grad {school}'],
    job: ['{job}', 'beruflich {job}', 'Job: {job}', 'arbeite im Bereich {job}'],
    hobby: ['{hobby}', 'liebe {hobby}', '{hobby} > alles', 'viel {hobby}', 'mag {hobby}', '{hobby} in {city}'],
    lyric: ['{lyric}', '"{lyric}"', '„{lyric}“'],
    oneLiner: ['{oneLiner}', '{oneLiner} haha', '{oneLiner} isso'],
    height: ['{height}', '{height} groß', 'bin {height}', '{height} falls wichtig'],
    pet: ['{pet}', '{pet}, Rest egal', 'wichtig: {pet}'],
  },
  formatHeight: (cm) => `${Math.floor(cm / 100)},${String(cm % 100).padStart(2, '0')}`,
  emojis: {
    general: [
      '🎵', '🎧', '🎬', '☕', '📚', '⚽', '🎮', '📷', '✈️', '🌊',
      '🏔️', '🐈', '🐶', '🌸', '✨', '🔥', '🌙', '☀️', '🍕', '🥨',
      '🍺', '🎤', '🌿', '😎', '💫', '🫶', '🎲', '🌅', '🍀', '🖤',
    ],
    female: ['🦋', '🌷', '💅', '🎀', '💗', '🌺', '🍓', '🫧'],
    male: ['💪', '🏋️', '🚗', '🏍️', '⚡', '🔧', '🥊', '🏆'],
  },
  imperfections: {
    substitutions: [
      [/\bich bin\b/gi, 'bin'],
      [/\bkeine ahnung\b/gi, 'kp'],
      [/\bhab dich lieb\b/gi, 'hdl'],
      [/\bliebe grüße\b/gi, 'lg'],
      [/\bgerade\b/gi, 'grad'],
      [/\bgar nicht\b/gi, 'garnich'],
      [/\bnicht\b/gi, 'nich'],
      [/\bhabe\b/gi, 'hab'],
      [/\bein bisschen\b/gi, 'bissl'],
      [/\bvielleicht\b/gi, 'vllt'],
      [/\bdas ist\b/gi, 'das is'],
      [/\bauf jeden fall\b/gi, 'auf jeden'],
      [/\bund so weiter\b/gi, 'usw'],
      [/\beinen\b/gi, 'nen'],
    ],
    lowercaseRate: 0.6,
    dropDiacriticsRate: 0.25,
    noFinalPunctRate: 0.75,
  },
  genderTopicBias: {
    female: { zodiac: 2.2, pet: 1.6, lyric: 1.3, hobby: 1.1, height: 0.4, team: 0.6 },
    male: { team: 2.2, height: 1.6, job: 1.3, zodiac: 0.6, pet: 0.8 },
  },
};
